"use client";

import { c as _c } from "react/compiler-runtime";
import { useEffect, useRef, useState } from "react";
import { Bell, BellRing, CheckCheck, X } from "lucide-react";
import { cn } from "@/shared/lib/utils";
import { useNotifications } from "../hooks/useNotifications";
import type { NotificationItem } from "../../types";
function timeAgo(dateStr: string): string {
  const diff = Date.now() - new Date(dateStr).getTime();
  const mins = Math.floor(diff / 60_000);
  if (mins < 1) return "just now";
  if (mins < 60) return `${mins}m ago`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs}h ago`;
  return `${Math.floor(hrs / 24)}d ago`;
}
function NotificationRow(t0) {
  const $ = _c(29);
  const {
    notification,
    onRead
  } = t0;
  const t1 = !notification.isRead && "bg-blue-50/40";
  let t2;
  if ($[0] !== t1) {
    t2 = cn("group w-full border-b border-slate-100 px-4 py-3 text-left transition-colors last:border-0 hover:bg-slate-50/80", t1);
    $[0] = t1;
    $[1] = t2;
  } else {
    t2 = $[1];
  }
  let t3;
  if ($[2] !== notification.id || $[3] !== notification.isRead || $[4] !== onRead) {
    t3 = () => {
      if (!notification.isRead) {
        onRead(notification.id);
      }
    };
    $[2] = notification.id;
    $[3] = notification.isRead;
    $[4] = onRead;
    $[5] = t3;
  } else {
    t3 = $[5];
  }
  const t4 = notification.isRead ? "bg-transparent" : "bg-blue-500";
  let t5;
  if ($[6] !== t4) {
    t5 = cn("mt-1.5 size-2 shrink-0 rounded-full", t4);
    $[6] = t4;
    $[7] = t5;
  } else {
    t5 = $[7];
  }
  let t6;
  if ($[8] !== t5) {
    t6 = <span className={t5} />;
    $[8] = t5;
    $[9] = t6;
  } else {
    t6 = $[9];
  }
  let t7;
  if ($[10] !== notification.title) {
    t7 = <p className="truncate text-sm font-semibold text-slate-900">{notification.title}</p>;
    $[10] = notification.title;
    $[11] = t7;
  } else {
    t7 = $[11];
  }
  let t8;
  if ($[12] !== notification.body) {
    t8 = <p className="mt-0.5 line-clamp-2 text-xs leading-relaxed text-slate-500">{notification.body}</p>;
    $[12] = notification.body;
    $[13] = t8;
  } else {
    t8 = $[13];
  }
  let t9;
  if ($[14] !== notification.createdAt) {
    t9 = timeAgo(notification.createdAt);
    $[14] = notification.createdAt;
    $[15] = t9;
  } else {
    t9 = $[15];
  }
  let t10;
  if ($[16] !== t9) {
    t10 = <p className="mt-1 text-[11px] text-slate-400">{t9}</p>;
    $[16] = t9;
    $[17] = t10;
  } else {
    t10 = $[17];
  }
  let t11;
  if ($[18] !== t10 || $[19] !== t7 || $[20] !== t8) {
    t11 = <div className="min-w-0 flex-1">{t7}{t8}{t10}</div>;
    $[18] = t10;
    $[19] = t7;
    $[20] = t8;
    $[21] = t11;
  } else {
    t11 = $[21];
  }
  let t12;
  if ($[22] !== t11 || $[23] !== t6) {
    t12 = <div className="flex items-start gap-3">{t6}{t11}</div>;
    $[22] = t11;
    $[23] = t6;
    $[24] = t12;
  } else {
    t12 = $[24];
  }
  let t13;
  if ($[25] !== t12 || $[26] !== t2 || $[27] !== t3) {
    t13 = <button className={t2} onClick={t3} type="button">{t12}</button>;
    $[25] = t12;
    $[26] = t2;
    $[27] = t3;
    $[28] = t13;
  } else {
    t13 = $[28];
  }
  return t13;
}
export function NotificationBell() {
  const $ = _c(24);
  const [open, setOpen] = useState(false);
  const ref = useRef(null);
  const {
    notifications,
    unreadCount,
    loading,
    markAsRead,
    markAllAsRead
  } = useNotifications();
  let t0;
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = () => {
      const handleClick = function handleClick(e) {
        if (ref.current && !ref.current.contains(e.target as Node)) {
          setOpen(false);
        }
      };
      document.addEventListener("mousedown", handleClick);
      return () => document.removeEventListener("mousedown", handleClick);
    };
    t1 = [];
    $[0] = t0;
    $[1] = t1;
  } else {
    t0 = $[0];
    t1 = $[1];
  }
  useEffect(t0, t1);
  const BellIcon = unreadCount > 0 ? BellRing : Bell;
  const t2 = `Notifications${unreadCount > 0 ? ` (${unreadCount} unread)` : ""}`;
  let t3;
  if ($[2] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = () => setOpen(_temp);
    $[2] = t3;
  } else {
    t3 = $[2];
  }
  const t4 = unreadCount > 0 && "animate-[ring_0.5s_ease-in-out]";
  let t5;
  if ($[3] !== t4) {
    t5 = cn("size-4.5", t4);
    $[3] = t4;
    $[4] = t5;
  } else {
    t5 = $[4];
  }
  let t6;
  if ($[5] !== BellIcon || $[6] !== t5) {
    t6 = <BellIcon className={t5} />;
    $[5] = BellIcon;
    $[6] = t5;
    $[7] = t6;
  } else {
    t6 = $[7];
  }
  let t7;
  if ($[8] !== unreadCount) {
    t7 = unreadCount > 0 && <span className="absolute -top-0.5 -right-0.5 flex size-4 items-center justify-center rounded-full bg-red-500 text-[10px] font-bold text-white shadow">{unreadCount > 9 ? "9+" : unreadCount}</span>;
    $[8] = unreadCount;
    $[9] = t7;
  } else {
    t7 = $[9];
  }
  let t8;
  if ($[10] !== t2 || $[11] !== t6 || $[12] !== t7) {
    t8 = <button aria-label={t2} className="relative inline-flex size-10 items-center justify-center rounded-full border border-white/16 bg-white/10 text-white backdrop-blur-sm transition hover:bg-white/16" onClick={t3} type="button">{t6}{t7}</button>;
    $[10] = t2;
    $[11] = t6;
    $[12] = t7;
    $[13] = t8;
  } else {
    t8 = $[13];
  }
  let t9;
  if ($[14] !== loading || $[15] !== markAllAsRead || $[16] !== markAsRead || $[17] !== notifications || $[18] !== open || $[19] !== unreadCount) {
    t9 = open && <div className="absolute top-12 right-0 z-50 w-80 overflow-hidden rounded-2xl border border-slate-200/80 bg-white shadow-[0_24px_64px_rgba(15,23,42,0.12)] ring-1 ring-slate-200/50"><div className="flex items-center justify-between border-b border-slate-100 px-4 py-3"><p className="text-sm font-semibold text-slate-900">Notifications</p><div className="flex items-center gap-2">{unreadCount > 0 && <button className="inline-flex items-center gap-1 rounded-full px-2 py-1 text-[11px] font-medium text-slate-500 transition-colors hover:bg-slate-100 hover:text-slate-900" onClick={markAllAsRead} title="Mark all as read" type="button"><CheckCheck className="size-3.5" />Mark all read</button>}<button aria-label="Close" className="inline-flex size-6 items-center justify-center rounded-full text-slate-400 transition-colors hover:bg-slate-100 hover:text-slate-700" onClick={() => setOpen(false)} type="button"><X className="size-3.5" /></button></div></div><div className="max-h-80 overflow-y-auto">{loading ? <div className="px-4 py-6 text-center text-sm text-slate-400">Loading…</div> : notifications.length === 0 ? <div className="px-4 py-8 text-center"><Bell className="mx-auto mb-2 size-8 text-slate-200" /><p className="text-sm text-slate-400">No notifications yet</p></div> : notifications.map(n => <NotificationRow key={n.id} notification={n} onRead={markAsRead} />)}</div></div>;
    $[14] = loading;
    $[15] = markAllAsRead;
    $[16] = markAsRead;
    $[17] = notifications;
    $[18] = open;
    $[19] = unreadCount;
    $[20] = t9;
  } else {
    t9 = $[20];
  }
  let t10;
  if ($[21] !== t8 || $[22] !== t9) {
    t10 = <div className="relative" ref={ref}>{t8}{t9}</div>;
    $[21] = t8;
    $[22] = t9;
    $[23] = t10;
  } else {
    t10 = $[23];
  }
  return t10;
}
function _temp(o) {
  return !o;
}